import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface ChecklistItem {
  id: string;
  label: string;
  description: string;
  completed: boolean;
}

export const GettingStartedChecklist = () => {
  const { toast } = useToast();
  const [dismissed, setDismissed] = useState(false);
  const [items, setItems] = useState<ChecklistItem[]>([
    {
      id: "profile",
      label: "Complete your profile",
      description: "Add your contact information and role",
      completed: false
    },
    {
      id: "tour",
      label: "Take the platform tour",
      description: "Learn about key features",
      completed: false
    },
    {
      id: "help",
      label: "Visit the Help Center",
      description: "Explore guides and documentation",
      completed: false
    }
  ]);

  useEffect(() => {
    loadProgress();
  }, []);

  const loadProgress = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("user_help_progress")
        .select("*")
        .eq("user_id", user.id)
        .single();

      if (error && error.code !== "PGRST116") {
        console.error("Error loading help progress:", error);
        return;
      }

      if (data) {
        setDismissed(data.checklist_dismissed);
        if (data.completed_tutorials && data.completed_tutorials.length > 0) {
          setItems(prevItems =>
            prevItems.map(item => ({
              ...item,
              completed: data.completed_tutorials.includes(item.id)
            }))
          );
        }
      }
    } catch (error) {
      console.error("Error loading help progress:", error);
    }
  };

  const handleDismiss = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from("user_help_progress")
        .upsert({
          user_id: user.id,
          checklist_dismissed: true
        });

      if (error) throw error;

      setDismissed(true);
      toast({
        title: "Checklist dismissed",
        description: "You can always find help in the Help Center"
      });
    } catch (error) {
      console.error("Error dismissing checklist:", error);
      toast({
        title: "Error",
        description: "Failed to save preferences",
        variant: "destructive"
      });
    }
  };

  const toggleItem = async (itemId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const newItems = items.map(item =>
        item.id === itemId ? { ...item, completed: !item.completed } : item
      );
      setItems(newItems);

      const completedIds = newItems.filter(i => i.completed).map(i => i.id);

      const { error } = await supabase
        .from("user_help_progress")
        .upsert({
          user_id: user.id,
          completed_tutorials: completedIds
        });

      if (error) throw error;
    } catch (error) {
      console.error("Error updating checklist:", error);
    }
  };

  if (dismissed || items.every(item => item.completed)) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle>Getting Started</CardTitle>
            <CardDescription>
              Complete these steps to get the most out of AthleteNexus
            </CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={handleDismiss}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {items.map(item => (
          <div
            key={item.id}
            className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
            onClick={() => toggleItem(item.id)}
          >
            <div
              className={`flex items-center justify-center w-5 h-5 rounded border-2 transition-colors ${
                item.completed
                  ? "bg-primary border-primary"
                  : "border-muted-foreground"
              }`}
            >
              {item.completed && <Check className="h-3 w-3 text-primary-foreground" />}
            </div>
            <div className="flex-1">
              <p className={`font-medium ${item.completed ? "line-through text-muted-foreground" : ""}`}>
                {item.label}
              </p>
              <p className="text-sm text-muted-foreground">{item.description}</p>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};